import { Settings } from "@/lib/icons";

export type TempUnit = "°C" | "°F" | "K";
export type DensityUnit = "g/cm³" | "kg/m³" | "lb/ft³";
export type EnergyUnit = "kJ/mol" | "eV";

export interface LabUnits {
  temperature: TempUnit;
  density: DensityUnit;
  energy: EnergyUnit;
}

export const defaultLabUnits: LabUnits = {
  temperature: "°C",
  density: "g/cm³",
  energy: "kJ/mol",
};

interface LabSettingsProps {
  units: LabUnits;
  onUnitsChange: (units: LabUnits) => void;
}

function UnitToggle<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: T[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-foreground">{label}</span>
      <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/50 border border-border">
        {options.map((opt) => (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              value === opt
                ? "bg-background text-foreground shadow-sm border border-border"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function LabSettings({ units, onUnitsChange }: LabSettingsProps) {
  return (
    <div className="max-w-lg mx-auto">
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-6">
          <Settings size={16} className="text-primary" />
          <h2 className="text-sm font-medium text-foreground">Global Units</h2>
        </div>

        <div className="flex flex-col gap-5">
          <UnitToggle<TempUnit>
            label="Temperature"
            options={["°C", "°F", "K"]}
            value={units.temperature}
            onChange={(v) => onUnitsChange({ ...units, temperature: v })}
          />
          <UnitToggle<DensityUnit>
            label="Density"
            options={["g/cm³", "kg/m³", "lb/ft³"]}
            value={units.density}
            onChange={(v) => onUnitsChange({ ...units, density: v })}
          />
          <UnitToggle<EnergyUnit>
            label="Energy"
            options={["kJ/mol", "eV"]}
            value={units.energy}
            onChange={(v) => onUnitsChange({ ...units, energy: v })}
          />
        </div>
      </div>
    </div>
  );
}
